import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { AuthLayout, AuthField, SubmitButton } from "@/components/auth/AuthLayout";
import { PasswordRules, passwordOk } from "@/components/auth/PasswordRules";
import { PasswordInput } from "@/pages/Login";
import { SessionsCard } from "@/components/auth/SessionsCard";
import { useApp } from "@/context/AppContext";
import { homeFor } from "@/lib/access";
import { api, errMsg } from "@/lib/api";

export const NewPasswordFields = ({ pw, setPw, confirm, setConfirm }) => (
  <>
    <AuthField label="New password"><PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" testId="new-password-input" /></AuthField>
    <PasswordRules value={pw} />
    <AuthField label="Confirm new password"><PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" testId="confirm-password-input" /></AuthField>
    {confirm && confirm !== pw && <p className="text-xs text-[#E60000] -mt-3" data-testid="password-mismatch">Passwords do not match</p>}
  </>
);

export default function ChangePassword() {
  const { auth, signIn, logout } = useApp();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  if (auth === null) return <div className="min-h-screen grid place-items-center text-gray-500">Loading…</div>;
  if (!auth) return <Navigate to="/login" replace />;
  const forced = auth.must_change_password;
  const codeLogin = forced && auth.kind === "member";

  const submit = async (e) => {
    e.preventDefault();
    if (!passwordOk(pw)) return toast.error("Your new password does not meet the rules");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: current, new_password: pw });
      signIn(r.data);
      toast.success(r.data.other_sessions_ended ? `Password updated. You were signed out on ${r.data.other_sessions_ended} other device${r.data.other_sessions_ended === 1 ? "" : "s"}.` : "Password updated");
      nav(r.data.kind === "member" ? "/my" : homeFor(r.data));
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <form onSubmit={submit} data-testid="change-password-form">
        <div className="w-11 h-11 rounded-md bg-[#FFF0F0] text-[#E60000] grid place-items-center mb-4"><ShieldCheck className="w-6 h-6" /></div>
        <h2 className="font-heading text-3xl font-extrabold tracking-tight">{codeLogin ? "Set a new password" : forced ? "Set your permanent password" : "Change password"}</h2>
        <p className="text-sm text-gray-500 mt-1">{codeLogin ? `Hello ${auth.first_name || auth.name}. You signed in with an SMS code. Choose a new password to continue. Any other devices will be signed out.` : forced ? `Hello ${auth.name}. Before you continue, replace the default password with one only you know.` : "Enter your current password and choose a new one. Any other devices you are signed in on will be signed out."}</p>
        <div className="mt-8 space-y-5">
          {!codeLogin && <AuthField label={forced ? "Default password" : "Current password"}><PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} testId="current-password-input" /></AuthField>}
          <NewPasswordFields pw={pw} setPw={setPw} confirm={confirm} setConfirm={setConfirm} />
          <SubmitButton busy={busy} testId="change-password-submit">{busy ? "Saving…" : "Save password"}</SubmitButton>
          {forced
            ? <button type="button" onClick={async () => { await logout(); nav("/"); }} className="text-sm text-gray-500 hover:text-[#E60000]" data-testid="change-password-signout">Sign out</button>
            : <button type="button" onClick={() => nav(-1)} className="text-sm text-gray-500 hover:text-[#E60000]" data-testid="change-password-cancel">Cancel</button>}
        </div>
      </form>
      {!forced && <div className="mt-8"><SessionsCard /></div>}
    </AuthLayout>
  );
}
